import { FormEvent, useMemo } from 'react';
import { Info, Loader2 } from 'lucide-react';
import InputField from '../../../components/Forms/Input/InputField';
import SelectField from '../../../components/Forms/Input/SelectField';
import FormModal from '../../../components/Modals/FormModal';
import RestaurantFormHelp from '../shared/RestaurantFormHelp';
import { RestaurantRawMaterialDTO } from '../../../types/restaurant/RestaurantRawMaterialTypes';
import { RestaurantInventoryForm } from '../../../types/restaurant/RestaurantInventoryTypes';

interface Props {
  isOpen: boolean;
  form: RestaurantInventoryForm;
  rawMaterials: RestaurantRawMaterialDTO[];
  unavailableRawMaterialIds: number[];
  saving: boolean;
  onClose: () => void;
  onChange: <K extends keyof RestaurantInventoryForm>(
    field: K,
    value: RestaurantInventoryForm[K],
  ) => void;
  onSave: () => void;
}

const numberFields = [
  'currentStock',
  'minimumStock',
  'maximumStock',
  'averageCost',
  'lastPurchaseCost',
] as const;

const RestaurantInventoryFormModal = ({
  isOpen,
  form,
  rawMaterials,
  unavailableRawMaterialIds,
  saving,
  onClose,
  onChange,
  onSave,
}: Props) => {
  const availableMaterials = useMemo(
    () => rawMaterials.filter((item) =>
      form.id ? item.id === form.rawMaterialId : item.active && !unavailableRawMaterialIds.includes(item.id),
    ),
    [form.id, rawMaterials, unavailableRawMaterialIds],
  );

  const errors = useMemo(() => {
    const result: Record<string, string> = {};
    if (!form.rawMaterialId) result.rawMaterialId = 'Seleccione una materia prima.';
    numberFields.forEach((field) => {
      if (form[field] !== '' && Number(form[field]) < 0) {
        result[field] = 'No puede ser negativo.';
      }
    });
    if (
      form.maximumStock !== '' &&
      Number(form.maximumStock) < Number(form.minimumStock || 0)
    ) {
      result.maximumStock = 'No puede ser menor que el stock mínimo.';
    }
    return result;
  }, [form]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!Object.keys(errors).length) onSave();
  };

  const numericChange = (field: typeof numberFields[number], value: string) =>
    onChange(field, value === '' ? '' : Number(value));

  return (
    <FormModal
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      title={form.id ? 'Editar inventario' : 'Agregar al inventario'}
      maxWidth="max-w-3xl"
      headerExtra={<RestaurantFormHelp topic="inventory" />}
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="flex gap-3 rounded-lg border border-primary/15 bg-primary/5 p-4 text-sm">
          <Info className="h-5 w-5 shrink-0 text-primary" />
          <p>
            Este módulo configura el saldo y los límites por sucursal. Los movimientos,
            compras y recetas se gestionan en sus módulos correspondientes.
          </p>
        </div>

        <SelectField
          label="Materia prima"
          name="rawMaterialId"
          value={form.rawMaterialId}
          iconKey="product"
          required
          disabled={Boolean(form.id)}
          error={errors.rawMaterialId}
          options={availableMaterials.map((item) => ({
            value: item.id,
            label: `${item.code} · ${item.name} (${item.unitName})`,
          }))}
          onChange={(event) => onChange('rawMaterialId', Number(event.target.value))}
        />

        {!form.id && availableMaterials.length === 0 && (
          <p className="-mt-2 rounded bg-warning/10 px-4 py-3 text-sm text-warning">
            Todas las materias primas activas de esta sucursal ya tienen inventario.
          </p>
        )}

        <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2">
          <InputField label="Stock actual" name="currentStock" type="number" iconKey="stock" value={form.currentStock} min={0} step="0.000001" error={errors.currentStock} onChange={(e) => numericChange('currentStock', e.target.value)} />
          <InputField label="Stock mínimo" name="minimumStock" type="number" iconKey="warning" value={form.minimumStock} min={0} step="0.000001" error={errors.minimumStock} onChange={(e) => numericChange('minimumStock', e.target.value)} />
          <InputField label="Stock máximo (opcional)" name="maximumStock" type="number" iconKey="stock" value={form.maximumStock} min={0} step="0.000001" error={errors.maximumStock} onChange={(e) => numericChange('maximumStock', e.target.value)} />
          <InputField label="Costo promedio" name="averageCost" type="number" iconKey="dollar" value={form.averageCost} min={0} step="0.000001" error={errors.averageCost} onChange={(e) => numericChange('averageCost', e.target.value)} />
          <InputField label="Último costo de compra" name="lastPurchaseCost" type="number" iconKey="dollar" value={form.lastPurchaseCost} min={0} step="0.000001" error={errors.lastPurchaseCost} onChange={(e) => numericChange('lastPurchaseCost', e.target.value)} />
        </div>

        <div className="flex justify-end gap-3 border-t border-stroke pt-5 dark:border-strokedark">
          <button type="button" onClick={onClose} disabled={saving} className="rounded border border-stroke px-5 py-2.5 dark:border-strokedark">
            Cancelar
          </button>
          <button type="submit" disabled={saving || !availableMaterials.length || Boolean(Object.keys(errors).length)} className="inline-flex items-center gap-2 rounded bg-primary px-5 py-2.5 font-medium text-white disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar inventario
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default RestaurantInventoryFormModal;
